import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { NzModalService } from 'ng-zorro-antd/modal';
import { MEDIA_TEXTS } from './media-errors';

/**
 * Leaving the page of a size while a file of the tab «Фото и видео» is still going up would stop the upload without a word (the tab is destroyed, the upload aborted) and leave
 * the half-made item behind. The open tab tells this service whether a file is on its way; the route asks it before the page is left, and the editor chooses to stay or to go.
 * With no upload going it answers «go» at once and nothing is shown, so the page behaves as it did.
 */
@Injectable({ providedIn: 'root' })
export class MediaLeaveGuard {
  private readonly modal = inject(NzModalService);
  private readonly translate = inject(TranslocoService);
  private readonly sources = new Set<() => boolean>();
  private asking: Promise<boolean> | null = null;

  /** The tab says whether it has a file on its way. Gives the function that takes the tab out again (when it is destroyed). */
  register(uploading: () => boolean): () => void {
    this.sources.add(uploading);
    return () => {
      this.sources.delete(uploading);
    };
  }

  /** `true` when nothing is going up; otherwise the question, which answers `true` when the editor chooses to go. */
  confirmLeave(): boolean | Promise<boolean> {
    if (![...this.sources].some(uploading => uploading())) {
      return true;
    }
    if (this.asking) {
      return this.asking;
    }
    const asked = new Promise<boolean>(resolve => {
      this.modal.confirm({
        nzTitle: this.t('leave.title'),
        nzContent: this.t('leave.text'),
        nzOkText: this.t('leave.ok'),
        nzOkDanger: true,
        nzCancelText: this.t('leave.cancel'),
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false)
      });
    });
    this.asking = asked;
    void asked.then(() => {
      this.asking = null;
    });
    return asked;
  }

  private t(key: string): string {
    return this.translate.translate(`${MEDIA_TEXTS}.${key}`);
  }
}

/** The guard of the routes of the page of a size (`canDeactivate` of the page, `canActivate` of the child pages that replace it): asks only while the tab has a file on its way. */
export const mediaLeaveGuard = (): boolean | Promise<boolean> => inject(MediaLeaveGuard).confirmLeave();
